import React from 'react';
import { Link, useParams, useRoutes } from 'react-router-dom';
import routes, {
  COMPANY_CONTRACTS,
  COMPANY_ESA_REPORTING,
  COMPANY_GOAL_ACHIEVEMENT,
  COMPANY_INFO,
} from '../routes';
import DocumentTitle from 'src/components/DocumentTitle/DocumentTitle';
import { LoadingPage } from 'src/components/LoadingPage/LoadingPage';
import useDocumentTitle from 'src/hooks/use-document-title';
import { useTranslation } from 'react-i18next';
import Page from 'src/components/Page/Page';
import SideNavigationMenu, { RouterLinks } from 'src/components/Layout/SideNavigationMenu/SideNavigationMenu';
import { ROUTES } from 'src/navigation';
import CompanyActiveTag from '../components/CompanyActiveTag';
import { PageParams } from '../types/PageParams';
import { convertIdToNumber } from '../utils/idConversion';
import { isSivaContractActive } from '../utils/isContractActive';
import SivaPhaseTag from '../../components/SivaPhaseTag';
import { getContractCurrentPhase } from '../utils/helpers';
import useCompanyContract from '../hooks/use-company-contract';
import { SivaPhase } from 'src/api/v2';
import PageWithSideComponents from 'src/components/Page/PageWithSideComponents';
import { useAuth } from 'src/features/authorization';

const CompanyDetails: React.FC = () => {
  const { contractId } = useParams<PageParams>();

  const { t: tCommon } = useTranslation();
  const { t: tSiva } = useTranslation('siva');
  const { t: tPageTitles } = useTranslation('pageTitles');
  const { documentTitle } = useDocumentTitle(tCommon('companies'));

  const { checkAccess } = useAuth();

  const { company, isLoading, contract } = useCompanyContract(convertIdToNumber(contractId));

  const element = useRoutes(routes);

  const activePhases = React.useMemo(() => {
    if (company) {
      const phases = company.contracts.map((contract) => getContractCurrentPhase(contract.phases));
      const uniquePhases = phases.filter((phase, index, self) => {
        return index === self.findIndex((t) => t?.sivaPhase === phase?.sivaPhase);
      });
      return uniquePhases;
    }
    return null;
  }, [company]);

  const companyContractsCount = company?.contracts.length ?? 1;

  const routerLinks: RouterLinks[] = [
    {
      id: 3,
      isVisible: true,
      to: ROUTES.SIVA.IDEAS_AND_COMPANIES.COMPANY_DETAILS.INDEX(contract?.numericId.toString() || ''),
      icon: { prefix: 'fal', iconName: 'envelope-open-text' },
      textContent:
        contract?.phases.at(-1)?.sivaPhase === SivaPhase.Idea
          ? `${tSiva('phase.Idea')}`
          : `${tSiva('company.details.sidenav.contracts', {
              count: 1,
            })}`,
      exact: true,
    },

    {
      id: 0,
      isVisible: !!company,
      to: COMPANY_INFO,
      icon: { prefix: 'fal', iconName: 'memo' },
      textContent: tSiva('company.details.sidenav.info'),
    },
    {
      id: 2,
      isVisible: contract?.phases.at(-1)?.sivaPhase !== SivaPhase.Idea,
      to: COMPANY_ESA_REPORTING,
      icon: { prefix: 'fal', iconName: 'money-check-dollar-pen' },
      textContent: tSiva('company.details.sidenav.ESAReporting'),
    },
    //TODO Conditionals på denne? Idéer?
  ];

  if (companyContractsCount > 1)
    routerLinks.push({
      id: 1,
      isVisible: true,
      to:
        companyContractsCount === 1 && contract
          ? ROUTES.SIVA.IDEAS_AND_COMPANIES.COMPANY_DETAILS.INDEX(contract.numericId.toString())
          : COMPANY_CONTRACTS,
      icon: { prefix: 'fal', iconName: 'file-contract' },
      textContent: `${tSiva('company.details.sidenav.contracts', {
        count: companyContractsCount,
      })}`,
    });

  if (checkAccess('FF_CompanyGoals').read)
    routerLinks.push({
      id: 4,
      isVisible: true,
      to: COMPANY_GOAL_ACHIEVEMENT,
      icon: { prefix: 'fal', iconName: 'chart-mixed' },
      textContent: tSiva('company.details.sidenav.GoalAchievement'),
    });

  if (isLoading) {
    return (
      <DocumentTitle title={documentTitle}>
        <Page noPadding>
          <LoadingPage />
        </Page>
      </DocumentTitle>
    );
  }

  return (
    <DocumentTitle title={documentTitle}>
      <PageWithSideComponents
        leftComponent={
          (company || contract?.phases.at(-1)?.sivaPhase !== SivaPhase.Idea) && (
            <SideNavigationMenu routerLinks={routerLinks} />
          )
        }
      >
        <div>
          <Link to={ROUTES.SIVA.IDEAS_AND_COMPANIES.BASE}>{tPageTitles('ideasAndCompanies')}</Link> {'>'}{' '}
          <span>Avtale</span>
          <div className="display--flex gap--1 margin-top--2">
            <CompanyActiveTag active={isSivaContractActive(contract)} />
            {activePhases &&
              activePhases.map((phase) => {
                return <SivaPhaseTag key={phase?.id} phase={phase?.sivaPhase} />;
              })}
          </div>
          <h1>{company?.name || contract?.name || contractId}</h1>
          <React.Suspense fallback={<LoadingPage />}>{element}</React.Suspense>
        </div>
      </PageWithSideComponents>
    </DocumentTitle>
  );
};

export default CompanyDetails;
